// HomeBookingWidget — full-width inquiry strip placed directly under
// the homepage hero. Lets visitors compose a multi-property
// accommodation inquiry where EACH selected hotel / bivouac / camp
// gets its own editable booking block (check-in, check-out, room /
// tent type, number of rooms / tents, adults, children, infants) —
// exactly like the per-property row on /booking. On submit (in
// legacy controlled mode) the widget deep-links into /booking with a
// JSON-encoded payload so every per-property detail survives the
// redirect; in standalone mode it reveals an inline Extras step
// followed by a Contact-details step, both submitted through the
// shared BookingContext so every surface sees the same enquiry.
//
// This file is intentionally a *composition* file. All heavy lifting
// lives in dedicated modules:
//   • useBookingBlocks       — derived blocks + CRUD mutations
//   • useCampPicker          — parent-category → camp modal flow
//   • useLocationModalState  — map-pin location detail modal
//   • useBookingFlowNav      — step refs + smooth-scroll wiring
//   • Booking*/HowItWorks*   — sub-component surfaces

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Plus, X } from "lucide-react";
import { HOTELS } from "@/lib/data";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBooking } from "@/contexts/BookingContext";
import { SERVICE_CATALOGUE } from "@/lib/serviceCatalogue";
import CampPickerModal from "@/components/booking/CampPickerModal";
import PropertyLocationModal from "@/components/booking/PropertyLocationModal";
import HowItWorksStrip from "@/components/booking/HowItWorksStrip";
import ItineraryStrip from "@/components/booking/ItineraryStrip";
import HotelSelectorBlock from "@/components/booking/HotelSelectorBlock";
import PropertyPickerVisual from "@/components/booking/PropertyPickerVisual";
import PropertyBlockList from "@/components/booking/PropertyBlockList";
import BookingGrandTotalStrip from "@/components/booking/BookingGrandTotalStrip";
import BookingFlowStep2 from "@/components/booking/BookingFlowStep2";
import BookingFlowStep3 from "@/components/booking/BookingFlowStep3";
import { COPY } from "@/components/booking/homeBookingWidgetCopy";
import useBookingBlocks from "@/hooks/useBookingBlocks";
import useCampPicker from "@/hooks/useCampPicker";
import useLocationModalState from "@/hooks/useLocationModalState";
import useBookingFlowNav from "@/hooks/useBookingFlowNav";
import useBookingEstimate from "@/lib/useBookingEstimate";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

// Two operating modes:
// • Standalone (default, used on the homepage): owns its own `blocks`
//   state through BookingContext; submit triggers the inline 3-step
//   flow (Accommodation → Extras → Details).
// • Embedded (used on `/booking` itself): receive `value` + `onChange`
//   from the parent so it stays in sync with the page-level
//   `selectedHotels` map. The submit + standalone header are hidden;
//   parent decides what the "Continue" CTA does.
const HomeBookingWidget = ({
  value,
  onChange,
  embedded = false,
  hideHeader = false,
  hideClearAll = false,
  hideSubmit = false,
  hideAddMore = false,
  onContinue,
  continueLabel,
  emptyHint,
} = {}) => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [hotelsOpen, setHotelsOpen] = useState(false);
  const widgetRef = useRef(null);

  // Refs used by the in-widget Step 3 (contact details) — focus the
  // first invalid field on validation error.
  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const phoneInputRef = useRef(null);

  const {
    selectedServiceKeys,
    validateContactDetails: _validateContactDetails,
    submit: contextSubmit,
    isSubmitting,
    requestClearAll,
  } = useBooking();

  // ── Derived blocks + CRUD (extracted to useBookingBlocks) ────────
  const {
    blocks,
    isControlled,
    mutate,
    updateBlock,
    duplicateBlock,
    removeBlock,
    applyDatesToAll,
  } = useBookingBlocks({ value, onChange });

  const isStandalone = !isControlled;

  // ── Camp picker + location modal state (extracted) ───────────────
  const {
    campPicker,
    toggleSlug,
    openCampEditor,
    closeCampPicker,
    onCampConfirmed,
  } = useCampPicker({ blocks, mutate });

  const {
    locationModalHotel,
    openLocationModal,
    closeLocationModal,
  } = useLocationModalState({ blocks, mutate });

  // ── Progressive step navigation (extracted) ──────────────────────
  const { flowStep, setFlowStep, goToStep, step2Ref, step3Ref } =
    useBookingFlowNav({ isStandalone });
  const step3Anchor = step3Ref; // alias — needed for validation focus fallback

  const hasSelections = blocks.length > 0 || selectedServiceKeys.length > 0;

  // Live ballpark estimates pulled from /api/pricing per selected slug.
  const { estimateForBlock, grandTotal } = useBookingEstimate(blocks);

  // Send Inquiry — submits via the shared context helper. On
  // validation error, focus the first invalid field + scroll.
  const handleSendInquiry = useCallback(async () => {
    const result = await contextSubmit({
      source: "home-widget",
      serviceCatalogue: SERVICE_CATALOGUE,
    });
    if (result?.reason === "validation") {
      const errs = result.errors || {};
      const firstInvalidRef =
        (errs.full_name && nameInputRef) ||
        (errs.email && emailInputRef) ||
        (errs.phone && phoneInputRef) ||
        null;
      const target = firstInvalidRef?.current || step3Anchor.current;
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => firstInvalidRef?.current?.focus?.(), 350);
    } else if (result?.ok) {
      // Successful submission — close the flow back to step 1 so the
      // user can start a fresh enquiry from the widget.
      setFlowStep(1);
    }
  }, [contextSubmit, setFlowStep, step3Anchor]);

  // Close popovers on outside click
  useEffect(() => {
    const onDocDown = (e) => {
      if (!widgetRef.current) return;
      if (!widgetRef.current.contains(e.target)) {
        setHotelsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, []);

  const distinctSelectedSlugs = useMemo(
    () => Array.from(new Set(blocks.map((b) => b.slug))),
    [blocks]
  );

  // Clear-All — defers to the shared context so the widget and the
  // floating StickySummary trigger the same confirmation dialog.
  const requestClearAllWidget = () => {
    if (isControlled) {
      // Controlled bridge stays consistent — parent owns `blocks`.
      mutate([]);
    }
    requestClearAll();
  };

  const hotelsSummary = useMemo(() => {
    if (blocks.length === 0) return pick(COPY.any_prop, lang);
    if (blocks.length === 1) {
      const h = HOTELS.find((x) => x.slug === blocks[0].slug);
      return h ? h.name : blocks[0].slug;
    }
    return `${blocks.length} ${pick(COPY.selected, lang)}`;
  }, [blocks, lang]);

  // Legacy controlled-mode submit — encode blocks and deep-link into
  // /booking so the page can rebuild the multi-room inquiry.
  const submit = useCallback(() => {
    if (blocks.length === 0) return;
    const items = blocks.map((b) => ({
      slug: b.slug,
      check_in: b.check_in,
      check_out: b.check_out,
      notes: b.notes,
      rooms: (b.rooms || []).map((r) => ({
        room_type: r.room_type || "",
        adults: Number(r.adults) || 2,
        children: Number(r.children) || 0,
        infants: Number(r.infants) || 0,
        board_basis: r.board_basis || "breakfast",
      })),
    }));
    const params = new URLSearchParams();
    params.set("items", JSON.stringify(items));
    navigate(`/booking?${params.toString()}`);
  }, [blocks, navigate]);

  const showHeaderRow =
    !hideHeader ||
    (!hideClearAll && (blocks.length > 0 || selectedServiceKeys.length > 0));

  return (
    <section
      ref={widgetRef}
      className="relative w-full bg-[#FDFBF7] border-b border-[#2C2621]/10"
      data-no-cms-text="true"
      data-testid="home-booking-widget"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4A373]/40 to-transparent" />

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-12 py-7 md:py-9">
        {showHeaderRow ? (
          <div className="flex items-baseline justify-between mb-4 md:mb-6">
            {!hideHeader ? (
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4A373]">
                  {pick(COPY.overline, lang)}
                </p>
                <h2 className="mt-1 font-serif-x text-xl md:text-2xl text-[#2C2621]">
                  {pick(COPY.title, lang)}
                </h2>
              </div>
            ) : (
              <span />
            )}
            {!hideClearAll &&
            (blocks.length > 0 || selectedServiceKeys.length > 0) ? (
              <button
                type="button"
                onClick={requestClearAllWidget}
                className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#A35133]"
                data-testid="home-booking-clear-all"
              >
                <X className="inline w-3 h-3 mr-1" strokeWidth={1.7} />
                {pick(COPY.clear_all, lang)}
              </button>
            ) : null}
          </div>
        ) : null}

        <HowItWorksStrip lang={lang} />

        <HotelSelectorBlock
          open={hotelsOpen}
          setOpen={setHotelsOpen}
          summary={hotelsSummary}
          blocks={blocks}
          selectedSlugs={distinctSelectedSlugs}
          onToggleSlug={toggleSlug}
          onEditCamp={openCampEditor}
          lang={lang}
        />

        <PropertyPickerVisual
          blocks={blocks}
          selectedSlugs={distinctSelectedSlugs}
          onToggle={toggleSlug}
          onEditCamp={openCampEditor}
          onOpenLocationModal={openLocationModal}
          lang={lang}
          emptyHint={emptyHint}
        />

        {blocks.length > 0 ? (
          <div className="mt-5 space-y-4">
            <PropertyBlockList
              blocks={blocks}
              lang={lang}
              today={today}
              onUpdate={updateBlock}
              onDuplicate={duplicateBlock}
              onRemove={removeBlock}
              onApplyDatesToAll={applyDatesToAll}
              estimateForBlock={estimateForBlock}
            />
            <div className="flex flex-col gap-3 pt-2">
              <ItineraryStrip blocks={blocks} lang={lang} />

              {grandTotal && !grandTotal.loading && grandTotal.total ? (
                <BookingGrandTotalStrip total={grandTotal.total} lang={lang} />
              ) : null}

              <div className="flex items-center justify-between gap-3">
                {!hideAddMore ? (
                  <button
                    type="button"
                    onClick={() => setHotelsOpen(true)}
                    className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542] inline-flex items-center gap-1.5"
                    data-testid="home-booking-add-more"
                  >
                    <Plus className="w-3.5 h-3.5" strokeWidth={1.7} />
                    {pick(COPY.cta_more, lang)}
                  </button>
                ) : (
                  <span />
                )}
                {(() => {
                  if (hideSubmit) return null;
                  if (isStandalone) {
                    // STANDALONE — progressive flow, no navigation.
                    if (flowStep === 1) {
                      return (
                        <button
                          type="button"
                          onClick={() => goToStep(2)}
                          disabled={!hasSelections}
                          className="inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-50 disabled:cursor-not-allowed text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors"
                          data-testid="home-booking-continue-extras"
                        >
                          {pick(COPY.cta_continue_extras, lang)}
                          <ArrowRight className="w-4 h-4" strokeWidth={1.6} />
                        </button>
                      );
                    }
                    return (
                      <span className="text-[10px] tracking-[0.25em] uppercase text-[#A07042] inline-flex items-center gap-2">
                        <Check className="w-3.5 h-3.5" strokeWidth={2} />
                        {pick(COPY.step_completed, lang)}
                      </span>
                    );
                  }
                  // CONTROLLED — legacy "Continue to details" hand-off.
                  return (
                    <button
                      type="button"
                      onClick={embedded ? onContinue : submit}
                      className="inline-flex items-center justify-center gap-3 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-8 py-4 text-xs tracking-[0.25em] uppercase transition-colors"
                      data-testid="home-booking-submit"
                    >
                      {embedded
                        ? continueLabel ||
                          pick(
                            {
                              en: "Continue to details",
                              fr: "Continuer vers les détails",
                              es: "Continuar a los detalles",
                            },
                            lang
                          )
                        : pick(COPY.cta, lang)}
                      <ArrowRight className="w-4 h-4" strokeWidth={1.6} />
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : null}

        {isStandalone && flowStep >= 2 ? (
          <BookingFlowStep2
            ref={step2Ref}
            lang={lang}
            flowStep={flowStep}
            onBack={() => setFlowStep(1)}
            onContinue={() => goToStep(3)}
          />
        ) : null}

        {isStandalone && flowStep >= 3 ? (
          <BookingFlowStep3
            ref={step3Ref}
            lang={lang}
            isSubmitting={isSubmitting}
            onBack={() => setFlowStep(2)}
            onSend={handleSendInquiry}
            nameRef={nameInputRef}
            emailRef={emailInputRef}
            phoneRef={phoneInputRef}
          />
        ) : null}
      </div>

      <CampPickerModal
        open={!!campPicker}
        hotelSlug={campPicker?.hotelSlug || null}
        initialCampSlugs={campPicker?.initialCampSlugs || []}
        onClose={closeCampPicker}
        onConfirm={onCampConfirmed}
        lang={lang}
      />

      <PropertyLocationModal
        open={!!locationModalHotel}
        hotel={locationModalHotel}
        onClose={closeLocationModal}
      />
    </section>
  );
};

export default HomeBookingWidget;
